const sanitizeHTML = (text: string) => {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

// Helper function to escape regex special characters
const escapeRegExp = (string: string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export const parseMarkdown = (text: string, ignorePatterns: string[] = []) => {
    if (!text) return '';
    
    // Split text into lines for easier line-by-line processing
    let lines = text.split('\n');
    
    // Mark lines that should be completely preserved
    const preservedLines: boolean[] = new Array(lines.length).fill(false);
    
    // Identify lines that match the ignore patterns
    lines = lines.map((line, index) => {
        for (const pattern of ignorePatterns) {
            if (line.includes(pattern)) {
                preservedLines[index] = true;
                return line;
            }
        }
        return line;
    });
    
    // Process only the non-preserved lines
    lines = lines.map((line, index) => {
        if (preservedLines[index]) {
            return line; // Skip processing for preserved lines
        }
        
        // Sanitize HTML
        let processedLine = line.replace(/[<>]/g, match => ({
            '<': '&lt;',
            '>': '&gt;'
        }[match] as string));
        
        // Process headers
        if (processedLine.match(/^###\s/)) {
            const content = processedLine.substring(3).trim();
            return content ? `<h3>${sanitizeHTML(content)}</h3>` : processedLine;
        } else if (processedLine.match(/^##\s/)) {
            const content = processedLine.substring(2).trim();
            return content ? `<h2>${sanitizeHTML(content)}</h2>` : processedLine;
        } else if (processedLine.match(/^#\s/)) {
            const content = processedLine.substring(1).trim();
            return content ? `<h1>${sanitizeHTML(content)}</h1>` : processedLine;
        }
        
        // Process bold and italic
        processedLine = processedLine.replace(/(\*\*|__)(.*?)\1/g, (_, __, content) =>
            `<strong>${sanitizeHTML(content)}</strong>`);
        processedLine = processedLine.replace(/(\*|_)(.*?)\1/g, (_, __, content) =>
            `<em>${sanitizeHTML(content)}</em>`);
        
        // Process inline code (skip triple-backtick lines — handled later as code blocks)
        if (!processedLine.match(/^`{3}/)) {
            processedLine = processedLine.replace(/`(.*?)`/g, (_, content) =>
                `<code>${sanitizeHTML(content)}</code>`);
        }
        
        // Process links
        processedLine = processedLine.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, linkText, url) => {
            if (!linkText || !url) return match;
            
            const sanitizedText = sanitizeHTML(linkText.trim());
            const sanitizedUrl = url.trim();
            
            // URL validation - must be http/https or relative path, block javascript: and other dangerous protocols
            const isValidUrl = sanitizedUrl.length > 0 && 
                !sanitizedUrl.includes('<') && 
                !sanitizedUrl.includes('>') &&
                !sanitizedUrl.toLowerCase().startsWith('javascript:') &&
                !sanitizedUrl.toLowerCase().startsWith('data:') &&
                !sanitizedUrl.toLowerCase().startsWith('vbscript:');
            
            if (!isValidUrl) {
                return sanitizedText;
            }
            
            return `<a href="${sanitizedUrl}" rel="noopener noreferrer" target="_blank" class="text-primary underline hover:text-primary/80">${sanitizedText}</a>`;
        });
        
        return processedLine;
    });
    
    // Process multi-line elements (code blocks, lists)
    let processedText = lines.join('\n');
    
    // Handle code blocks with ``` (which can span multiple lines)
    processedText = processedText.replace(/```\n?([\s\S]*?)\n?```/g, (_, content) => {
        // Check if this block should be preserved
        if (ignorePatterns.some(pattern => content.includes(pattern))) {
            return `\`\`\`${content}\`\`\``;
        }
        // Replace newlines with <br> inside code blocks
        const formattedContent = content.trim().split('\n').join('<br>');
        return `<pre><code><br>${formattedContent}<br></code></pre>`;
    });
    
    // Handle lists (both ordered and unordered)
    const processLists = (text: string) => {
        const lines = text.split('\n');
        const result: string[] = [];
        let i = 0;

        // Helper: collect nested sub-list items of a different type (e.g. unordered bullets under an ordered item)
        const collectSubList = (startIdx: number, parentIsOrdered: boolean): { html: string; endIdx: number } => {
            const subItems: string[] = [];
            const subIsOrdered = !parentIsOrdered;
            const subListType = subIsOrdered ? 'ol' : 'ul';
            const subListClass = subIsOrdered ? 'list-decimal' : 'list-disc';
            let j = startIdx;

            while (j < lines.length) {
                const line = lines[j];
                const uMatch = line.match(/^(\s*)([\*\-\+])\s+(.+)$/);
                const oMatch = line.match(/^(\s*)\d+\.\s+(.+)$/);
                const lineIsOrdered = !!oMatch;
                const isListItem = !!(uMatch || oMatch);

                if (isListItem && lineIsOrdered === subIsOrdered) {
                    const content = oMatch ? oMatch[2] : uMatch![3];
                    subItems.push(`<li class="pl-2">${content}</li>`);
                    j++;
                } else if (line.trim() === '') {
                    // Empty line — peek ahead
                    let next = j + 1;
                    while (next < lines.length && lines[next].trim() === '') next++;
                    if (next < lines.length) {
                        const nextU = lines[next].match(/^(\s*)([\*\-\+])\s+(.+)$/);
                        const nextO = lines[next].match(/^(\s*)\d+\.\s+(.+)$/);
                        const nextIsOrdered = !!nextO;
                        if ((nextU || nextO) && nextIsOrdered === subIsOrdered) {
                            j++;
                            continue;
                        }
                    }
                    break;
                } else {
                    break;
                }
            }

            const html = subItems.length > 0
                ? `<${subListType} class="${subListClass} pl-6">${subItems.join('')}</${subListType}>`
                : '';
            return { html, endIdx: j };
        };

        // Helper: check if the next non-empty line is a list item and return its type
        const peekNextListItem = (startIdx: number): { isOrdered: boolean; found: boolean; idx: number } => {
            let j = startIdx;
            while (j < lines.length && lines[j].trim() === '') j++;
            if (j >= lines.length) return { isOrdered: false, found: false, idx: j };
            const uMatch = lines[j].match(/^(\s*)([\*\-\+])\s+(.+)$/);
            const oMatch = lines[j].match(/^(\s*)\d+\.\s+(.+)$/);
            if (uMatch || oMatch) return { isOrdered: !!oMatch, found: true, idx: j };
            return { isOrdered: false, found: false, idx: j };
        };

        while (i < lines.length) {
            const line = lines[i];

            // Check if line should be preserved
            if (ignorePatterns.some(pattern => line.includes(pattern))) {
                result.push(line);
                i++;
                continue;
            }

            // Check if this is a list item
            const unorderedMatch = line.match(/^(\s*)([\*\-\+])\s+(.+)$/);
            const orderedMatch = line.match(/^(\s*)\d+\.\s+(.+)$/);

            if (unorderedMatch || orderedMatch) {
                // Start collecting list items
                const isOrdered = !!orderedMatch;
                const listType = isOrdered ? 'ol' : 'ul';
                const listClass = isOrdered ? 'list-decimal' : 'list-disc';
                const items: string[] = [];

                // Collect all consecutive list items (allowing empty lines and nested sub-lists)
                while (i < lines.length) {
                    const currentLine = lines[i];
                    const currentUnordered = currentLine.match(/^(\s*)([\*\-\+])\s+(.+)$/);
                    const currentOrdered = currentLine.match(/^(\s*)\d+\.\s+(.+)$/);
                    const currentIsOrdered = !!currentOrdered;

                    if (currentUnordered || currentOrdered) {
                        if (currentIsOrdered === isOrdered) {
                            // Same type — add as list item
                            const content = currentOrdered ? currentOrdered[2] : currentUnordered![3];
                            items.push(`<li class="pl-2">${content}`);
                            i++;

                            // Check if next lines are a nested sub-list of the opposite type
                            const peek = peekNextListItem(i);
                            if (peek.found && peek.isOrdered !== isOrdered) {
                                // Skip blank lines to reach the sub-list
                                i = peek.idx;
                                const sub = collectSubList(i, isOrdered);
                                if (sub.html) {
                                    // Append sub-list inside the current <li>
                                    items[items.length - 1] += sub.html;
                                    i = sub.endIdx;
                                }
                            }

                            // Close the <li>
                            items[items.length - 1] += '</li>';
                        } else {
                            // Different list type at top level — stop
                            break;
                        }
                    } else if (currentLine.trim() === '') {
                        // Empty line — look ahead for next same-type list item (possibly after sub-list)
                        const peek = peekNextListItem(i + 1);
                        if (peek.found && peek.isOrdered === isOrdered) {
                            i++;
                            continue;
                        }
                        // Also check if next is a sub-list followed by same-type parent item
                        if (peek.found && peek.isOrdered !== isOrdered) {
                            // Could be sub-list items for the previous <li>
                            // Only attach if we already have items
                            if (items.length > 0) {
                                i = peek.idx;
                                const sub = collectSubList(i, isOrdered);
                                if (sub.html) {
                                    // Re-open last item, append sub-list
                                    items[items.length - 1] = items[items.length - 1].replace(/<\/li>$/, sub.html + '</li>');
                                    i = sub.endIdx;
                                    continue;
                                }
                            }
                        }
                        // Not followed by related list content, end the list
                        break;
                    } else {
                        // Non-empty, non-list line — check if it's indented continuation
                        if (items.length > 0 && currentLine.match(/^\s+/)) {
                            items[items.length - 1] = items[items.length - 1].replace(/<\/li>$/, ` ${currentLine.trim()}</li>`);
                            i++;
                        } else {
                            break;
                        }
                    }
                }

                // Output the complete list
                if (items.length > 0) {
                    result.push(`<${listType} class="${listClass} pl-8">${items.join('')}</${listType}>`);
                }
            } else {
                result.push(line);
                i++;
            }
        }

        return result.join('\n');
    };

    processedText = processLists(processedText);
    
    // Handle line breaks - convert single newlines to <br> and double newlines to paragraph breaks
    processedText = processedText
        .split(/\n\s*\n+/)  // split on multiple newlines (paragraphs)
        .map(p => p.trim())  // trim each paragraph
        .filter(p => p)      // remove empty paragraphs
        .map(p => p.replace(/\n/g, '<br>'))  // convert single newlines within paragraphs to <br>
        .join('<br><br>');   // join paragraphs with double <br>
    
    return processedText;
}
